/**
 * Filter Visibility Menu
 *
 * Popup to activate/deactivate which table filters appear in the filter bar.
 */

"use client";

import { cn } from "@/lib/utils/general-utils";
import { Filter as FunnelIcon, Search } from "lucide-react";
import * as React from "react";
import { createPortal } from "react-dom";

const { useMemo, useState, useRef, useEffect } = React;

export interface FilterVisibilityItem {
  id: string;
  label: string;
}

export interface FilterVisibilityMenuProps {
  readonly filters: FilterVisibilityItem[];
  readonly enabledIds: string[];
  readonly onToggle: (_filterId: string, _enabled: boolean) => void;
  readonly onSetAll?: (_enabled: boolean) => void;
  /** Filter IDs that currently have a value selected (independent of visibility). */
  readonly appliedIds?: string[];
  readonly buttonClassName?: string;
  readonly size?: "sm" | "md";
}

export function FilterVisibilityMenu({
  filters,
  enabledIds,
  onToggle,
  onSetAll,
  appliedIds = [],
  buttonClassName,
  size = "sm",
}: Readonly<FilterVisibilityMenuProps>) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState("");
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    right: number;
  } | null>(null);

  const enabledSet = useMemo(() => new Set(enabledIds), [enabledIds]);
  const appliedSet = useMemo(() => new Set(appliedIds), [appliedIds]);
  const enabledCount = useMemo(
    () => filters.filter((filter) => enabledSet.has(filter.id)).length,
    [filters, enabledSet],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return filters;
    return filters.filter((filter) => filter.label.toLowerCase().includes(q));
  }, [filters, search]);

  const allVisible = enabledCount === filters.length && filters.length > 0;
  const noneVisible = enabledCount === 0;

  useEffect(() => {
    if (!menuOpen || !buttonRef.current) return;

    const updatePosition = () => {
      if (!buttonRef.current) return;
      const buttonRect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: buttonRect.bottom + window.scrollY + 8,
        right: window.innerWidth - buttonRect.right + window.scrollX,
      });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) {
      setMenuPosition(null);
      setSearch("");
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false);
        setMenuPosition(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (menuOpen) {
      searchRef.current?.focus();
    }
  }, [menuOpen]);

  const handleToggleMenu = () => {
    setMenuOpen((prev) => {
      if (prev) setMenuPosition(null);
      return !prev;
    });
  };

  const handleShowAll = () => {
    if (onSetAll) {
      onSetAll(true);
      return;
    }
    filters.forEach((filter) => {
      if (!enabledSet.has(filter.id)) onToggle(filter.id, true);
    });
  };

  const handleHideAll = () => {
    if (onSetAll) {
      onSetAll(false);
      return;
    }
    filters.forEach((filter) => {
      if (enabledSet.has(filter.id)) onToggle(filter.id, false);
    });
  };

  const buttonSizeClass =
    size === "md" ? "h-10 w-10 rounded-lg" : "h-8 w-8 rounded-md";

  const dropdownContent = menuOpen && menuPosition && (
    <div
      ref={menuRef}
      className={cn(
        "fixed w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[300]",
        "max-h-96 flex flex-col",
      )}
      style={{
        top: `${menuPosition.top}px`,
        right: `${menuPosition.right}px`,
      }}
      role="menu"
      aria-label="Filter visibility"
    >
      <div className="px-3 py-2 text-xs font-semibold text-text-tertiary uppercase tracking-wider border-b border-gray-200">
        Filters
      </div>

      <div className="px-3 py-2 border-b border-gray-200">
        <div className="relative">
          <Search
            className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400"
            aria-hidden="true"
          />
          <input
            ref={searchRef}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search filters…"
            className={cn(
              "block w-full h-8 pl-7 pr-2 text-sm border rounded-md",
              "placeholder:text-gray-400 text-gray-900",
              "focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300",
              "[&::-webkit-search-cancel-button]:appearance-none",
              "border-gray-200 bg-white",
            )}
            aria-label="Search filters"
          />
        </div>
      </div>

      <div className="px-3 py-2 border-b border-gray-200">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <button
            onClick={handleShowAll}
            disabled={allVisible}
            className="text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            type="button"
          >
            Show all
          </button>
          <span>•</span>
          <button
            onClick={handleHideAll}
            disabled={noneVisible}
            className="text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            type="button"
          >
            Hide all
          </button>
          <span className="ml-auto text-text-tertiary">
            {enabledCount} / {filters.length}
          </span>
        </div>
      </div>

      <div className="overflow-y-auto flex-1 min-h-0">
        {filtered.length === 0 ? (
          <p className="px-3 py-3 text-sm text-text-tertiary">
            No filters match
          </p>
        ) : (
          filtered.map((filter) => {
            const checked = enabledSet.has(filter.id);
            const applied = appliedSet.has(filter.id);
            return (
              <label
                key={filter.id}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors cursor-pointer",
                  "hover:bg-gray-50",
                  checked && "bg-primary/5",
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => onToggle(filter.id, e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 cursor-pointer accent-primary"
                  style={{ accentColor: "var(--primary)" }}
                  aria-label={
                    applied ? `${filter.label}, applied` : filter.label
                  }
                />
                <span
                  className={cn(
                    "font-medium flex-1 min-w-0 flex items-center gap-2",
                    checked ? "text-primary" : "text-gray-700",
                  )}
                >
                  <span className="truncate">{filter.label}</span>
                  {applied ? (
                    <span
                      className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                      aria-hidden="true"
                    >
                      Applied
                    </span>
                  ) : null}
                </span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <>
      <div className="relative" data-tour="filter-bar">
        <button
          ref={buttonRef}
          onClick={handleToggleMenu}
          className={cn(
            "inline-flex items-center justify-center border transition-all",
            "focus:outline-none",
            buttonSizeClass,
            menuOpen
              ? "bg-primary/10 text-primary border-primary/30"
              : "bg-white text-text-tertiary border-gray-200 hover:bg-gray-50 hover:text-gray-700",
            buttonClassName,
          )}
          type="button"
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          aria-label="Toggle filters"
        >
          <FunnelIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {typeof window !== "undefined" &&
        dropdownContent &&
        createPortal(dropdownContent, document.body)}
    </>
  );
}
